import { Navbar } from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Handshake, Building, Phone, Mail, Globe, Users } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const PartnerWithUs = () => {
  const { t, language } = useLanguage();

  // Translated partners data
  const partners = [
    {
      id: 1,
      name: t('partners.partnersList.0.name'),
      logo: t('partners.partnersList.0.name')[0],
      industry: t('partners.partnersList.0.industry'),
      since: t('partners.partnersList.0.since'),
      projects: t('partners.partnersList.0.projects'),
    },
    {
      id: 2,
      name: t('partners.partnersList.1.name'),
      logo: t('partners.partnersList.1.name')[0],
      industry: t('partners.partnersList.1.industry'),
      since: t('partners.partnersList.1.since'),
      projects: t('partners.partnersList.1.projects'),
    },
    {
      id: 3,
      name: t('partners.partnersList.2.name'),
      logo: t('partners.partnersList.2.name')[0],
      industry: t('partners.partnersList.2.industry'),
      since: t('partners.partnersList.2.since'),
      projects: t('partners.partnersList.2.projects'),
    },
    {
      id: 4,
      name: t('partners.partnersList.3.name'),
      logo: t('partners.partnersList.3.name')[0],
      industry: t('partners.partnersList.3.industry'),
      since: t('partners.partnersList.3.since'),
      projects: t('partners.partnersList.3.projects'),
    },
    {
      id: 5,
      name: t('partners.partnersList.4.name'),
      logo: t('partners.partnersList.4.name')[0],
      industry: t('partners.partnersList.4.industry'),
      since: t('partners.partnersList.4.since'),
      projects: t('partners.partnersList.4.projects'),
    },
    {
      id: 6,
      name: t('partners.partnersList.5.name'),
      logo: t('partners.partnersList.5.name')[0],
      industry: t('partners.partnersList.5.industry'),
      since: t('partners.partnersList.5.since'),
      projects: t('partners.partnersList.5.projects'),
    },
  ];

  return (
    <div className="min-h-screen" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="py-20 bg-hero-gradient text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <span className="text-sm font-semibold text-primary-foreground uppercase tracking-wide animate-fade-in-down" style={{ animationDelay: '0.1s' }}>
                {t('partners.hero.sectionTitle')}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mt-3 mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                {t('partners.hero.title')}
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                {t('partners.hero.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Partners Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {partners.map((partner) => (
                <div
                  key={partner.id}
                  className="bg-card rounded-2xl p-8 shadow-card hover:shadow-elevated transition-all duration-300"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-hero-gradient flex items-center justify-center">
                      <span className="text-primary-foreground text-xl font-bold">
                        {partner.logo}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{partner.name}</h3>
                      <span className="inline-block px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mt-1">
                        {partner.industry}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('common.since')}:</span>
                      <span className="font-medium">{partner.since}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('partners.impact.projects')}:</span>
                      <span className="font-medium">{partner.projects} {t('common.projects')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partnership Impact */}
        <section className="py-20 bg-sidebar text-sidebar-foreground">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('partners.impact.title')}</h2>
              <p className="text-sidebar-foreground/70 max-w-2xl mx-auto">
                {t('partners.impact.subtitle')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="bg-sidebar-accent rounded-2xl p-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-sidebar-primary flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-sidebar-primary-foreground" />
                </div>
                <h3 className="font-bold text-2xl mb-2">50+</h3>
                <p className="text-sidebar-foreground/70">{t('partners.impact.partnerships')}</p>
              </div>
              
              <div className="bg-sidebar-accent rounded-2xl p-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-sidebar-primary flex items-center justify-center mx-auto mb-4">
                  <Building className="w-8 h-8 text-sidebar-primary-foreground" />
                </div>
                <h3 className="font-bold text-2xl mb-2">120+</h3>
                <p className="text-sidebar-foreground/70">{t('partners.impact.projects')}</p>
              </div>
              
              <div className="bg-sidebar-accent rounded-2xl p-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-sidebar-primary flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-sidebar-primary-foreground" />
                </div>
                <h3 className="font-bold text-2xl mb-2">500M+</h3>
                <p className="text-sidebar-foreground/70">{t('partners.impact.investments')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Become a Partner */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="bg-card rounded-3xl p-8 md:p-12 shadow-card max-w-4xl mx-auto">
              <div className="text-center">
                <Handshake className="w-16 h-16 text-primary mx-auto mb-6" />
                <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('partners.cta.title')}</h2>
                <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
                  {t('partners.cta.description')}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                  <Button size="lg" className="gap-2 bg-hero-gradient hover:opacity-90 text-primary-foreground">
                    <Phone className="w-5 h-5" />
                    {t('partners.cta.contactNow')}
                  </Button>
                  <Button size="lg" variant="outline" className="gap-2">
                    <Mail className="w-5 h-5" />
                    {t('partners.cta.sendMessage')}
                  </Button>
                </div>
                
                <div className="bg-muted rounded-2xl p-6 max-w-xl mx-auto">
                  <h3 className="font-bold text-lg mb-4">{t('partners.cta.contactInfo')}</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-muted-foreground">
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-primary" />
                      <span>+213 XX XXX XXXX</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-primary" />
                      <span>partners@knowvest.plus</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PartnerWithUs;